/**
 * Purchases: every supplier invoice and return in a period, newest first.
 * A return shows as a negative row against the invoice it came from, and a
 * cancelled document stays listed, struck through -- the trail matters.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Skeleton, Stack, Typography, useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'

import { fetchPurchases } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type PurchaseRow } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import ReportShell, { rangeLabel } from '../reports/ReportShell'
import ReportToolbar from '../reports/ReportToolbar'
import { useDateRange } from '../reports/useDateRange'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'
import Keycap from '../components/Keycap'
import LedgerPicker from '../vouchers/LedgerPicker'
import { downloadCsv } from '../reports/csv'
import { gregorian } from '../reports/hijri'
import { c, fmtMoney } from '../theme'

export default function PurchasesPage() {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const nav = useNavigate()
  const { can } = useAuth()
  const narrow = useMediaQuery('(max-width:760px)')
  const ar = i18n.language === 'ar'
  const { range, setRange, presets } = useDateRange()
  const [supplier, setSupplier] = useState('')

  const q = useQuery({
    queryKey: ['inv-purchases', range.from, range.to, supplier],
    queryFn: () => fetchPurchases({ from_date: range.from, to_date: range.to,
                                    supplier_code: supplier || undefined }),
  })
  const mayPost = can(P.postPurchase)
  useHotkeys('inv-purchases', [
    { combo: 'alt+n', label: t('inv.newPurchase'), handler: () => nav('/inventory/purchases/new') },
  ], mayPost)

  const rows = q.data?.purchases ?? []
  const name = (r: PurchaseRow) => (ar && r.supplier_name_ar) || r.supplier_name
  const signed = (r: PurchaseRow, v: string) => (r.kind === 'RETURN' ? `−${fmtMoney(v)}` : fmtMoney(v))

  return (
    <ReportShell titleKey="nav.purchases" signatures={false}
      period={rangeLabel(range.from, range.to, i18n.language, t)}
      headRight={mayPost ? (
        <Button variant="contained" startIcon={<AddIcon />} className="no-print"
          onClick={() => nav('/inventory/purchases/new')}>
          {t('inv.newPurchase')}
          <Box component="span" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
            <Keycap combo="alt+n" /></Box>
        </Button>
      ) : undefined}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
          onExport={() => downloadCsv(`purchases-${range.from}-${range.to}`, [
            ['Date', 'Number', 'Kind', 'Supplier', 'Supplier invoice', 'Store',
             'Net', 'VAT', 'Total', 'Status'],
            ...rows.map((r) => [r.doc_date, r.doc_no, r.kind, r.supplier_name,
              r.supplier_invoice_no ?? '', r.store_name, r.subtotal, r.vat_amount,
              r.total, r.status]),
          ])}>
          <Box sx={{ minWidth: 260 }}>
            <LedgerPicker value={supplier} onChange={(code) => setSupplier(code)}
              groups={['CREDIT']} placeholder={t('inv.allSuppliers')} />
          </Box>
        </ReportToolbar>
      }>

      {q.data && (
        <FigureGrid>
          <Figure label={bi('inv.documents')} value={String(q.data.count)} />
          <Figure label={bi('inv.subtotal')} value={fmtMoney(q.data.totals.subtotal)} />
          <Figure label={bi('inv.inputVat')} value={fmtMoney(q.data.totals.vat_amount)} />
          <Figure label={bi('inv.total')} value={fmtMoney(q.data.totals.total)} strong />
        </FigureGrid>
      )}

      {q.isError ? (
        <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
      ) : q.isLoading ? (
        <Box sx={{ p: 2 }}><Skeleton /><Skeleton /></Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ p: 3, color: c.inkMuted }}>{t('inv.noPurchases')}</Typography>
      ) : narrow ? (
        <Box className="ruled">
          {rows.map((r) => (
            <Box key={r.id} component="button" type="button"
              onClick={() => nav(`/inventory/purchases/${r.id}`)}
              sx={{ display: 'flex', width: '100%', gap: 1.5, px: 2, py: 1.25, border: 0,
                    bgcolor: 'transparent', font: 'inherit', color: 'inherit', textAlign: 'start',
                    cursor: 'pointer', opacity: r.status === 'CANCELLED' ? 0.5 : 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap dir="auto" sx={{ fontWeight: 500 }}>{name(r)}</Typography>
                <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                  <span className="doc-no">{r.doc_no}</span> · {gregorian(r.doc_date, i18n.language)}
                </Typography>
              </Box>
              <Typography className="num" sx={{ fontWeight: 600 }}>{signed(r, r.total)}</Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
          <table className="rpt" style={{ minWidth: 880 }}>
            <thead><tr>
              <th style={{ width: 104 }}>{t('entry.date')}</th>
              <th style={{ width: 140 }}>{t('report.voucherNo')}</th>
              <th>{t('inv.supplier')}</th>
              <th style={{ width: 130 }}>{t('inv.supplierInvoiceNo')}</th>
              <th style={{ width: 140 }}>{t('inv.store')}</th>
              <th className="num" style={{ width: 110 }}>{t('inv.net')}</th>
              <th className="num" style={{ width: 96 }}>{t('inv.vat')}</th>
              <th className="num" style={{ width: 120 }}>{t('inv.total')}</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="clickable" onClick={() => nav(`/inventory/purchases/${r.id}`)}
                  style={{ opacity: r.status === 'CANCELLED' ? 0.5 : 1,
                           textDecoration: r.status === 'CANCELLED' ? 'line-through' : undefined }}>
                  <td style={{ whiteSpace: 'nowrap' }}>{gregorian(r.doc_date, i18n.language)}</td>
                  <td>
                    <span className="doc-no" style={{ color: 'var(--primary-ink)' }}>{r.doc_no}</span>
                    {r.kind === 'RETURN' && (
                      <Chip size="small" label={t('inv.returnChip')} sx={{ ml: 1, height: 18, fontSize: 10.5 }} />
                    )}
                  </td>
                  <td dir="auto">
                    {name(r)}
                    {r.paid_from && <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                      {t('inv.paidFromShort', { account: r.paid_from })}</div>}
                  </td>
                  <td>{r.supplier_invoice_no ?? '—'}</td>
                  <td>{r.store_name}</td>
                  <td className="num">{signed(r, r.subtotal)}</td>
                  <td className="num">{signed(r, r.vat_amount)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{signed(r, r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
      <Stack sx={{ px: 2.5, pb: 2 }}>
        <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{t('inv.purchasesNote')}</Typography>
      </Stack>
    </ReportShell>
  )
}
